/**
 * Logbook Service
 * 
 * Frontend service for interacting with logbook API.
 */

import { apiConfig } from "@/config/api";
import { apiFetch, UPLOAD_API_TIMEOUT_MS } from "@/lib/apiFetch";

export type Mode = "SSB" | "CW" | "FT8" | "FT4" | "PSK31" | "PSK63" | "RTTY" | "AM" | "FM" | "DIGITAL" | "OTHER";
export type QSLStatus = "NONE" | "SENT" | "RECEIVED" | "CONFIRMED";
export type Band = "160M" | "80M" | "40M" | "30M" | "20M" | "17M" | "15M" | "12M" | "10M" | "6M" | "2M" | "70CM" | "OTHER";

export interface LogbookEntry {
  _id: string;
  user: string;
  date: string;
  timeOn: string;
  timeOff?: string;
  callsign: string;
  operator?: string;
  frequency?: number;
  band: Band;
  mode: Mode;
  rstSent?: string;
  rstReceived?: string;
  power?: number;
  antenna?: string;
  equipment?: string;
  gridSquare?: string;
  country?: string;
  state?: string;
  dxcc?: number;
  cqZone?: number;
  ituZone?: number;
  distance?: number;
  bearing?: number;
  qslStatus: QSLStatus;
  qslSentDate?: string;
  qslReceivedDate?: string;
  qslVia?: string;
  contestName?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateLogbookEntryPayload {
  date: string;
  timeOn: string;
  timeOff?: string;
  callsign: string;
  operator?: string;
  frequency?: number;
  band: Band;
  mode: Mode;
  rstSent?: string;
  rstReceived?: string;
  power?: number;
  antenna?: string;
  equipment?: string;
  gridSquare?: string;
  country?: string;
  state?: string;
  dxcc?: number;
  cqZone?: number;
  ituZone?: number;
  distance?: number;
  bearing?: number;
  qslStatus?: QSLStatus;
  qslSentDate?: string;
  qslReceivedDate?: string;
  qslVia?: string;
  contestName?: string;
  notes?: string;
}

export interface LogbookEntryListResponse {
  data: LogbookEntry[];
  total: number;
}

export interface LogbookStatistics {
  totalContacts: number;
  uniqueCallsigns: number;
  uniqueCountries: number;
  uniqueDxcc: number;
  uniqueCqZones: number;
  uniqueItuZones: number;
  contactsByBand: Record<string, number>;
  contactsByMode: Record<string, number>;
  contactsByQslStatus: Record<string, number>;
  firstContact?: string;
  lastContact?: string;
}

class LogbookService {
  private async getAuthHeaders(): Promise<HeadersInit> {
    return {
      "Content-Type": "application/json",
    };
  }

  /**
   * Create a logbook entry
   */
  async createEntry(payload: CreateLogbookEntryPayload): Promise<LogbookEntry> {
    const headers = await this.getAuthHeaders();
    
    const response = await apiFetch(`${apiConfig.baseURL}/api/logbook`, {
      method: "POST",
      headers,
      credentials: "include",
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: "Failed to create logbook entry" }));
      throw new Error(error.error || error.message || "Failed to create logbook entry");
    }

    const data = await response.json();
    return data.entry;
  }

  /**
   * Update a logbook entry
   */
  async updateEntry(entryId: string, payload: Partial<CreateLogbookEntryPayload>): Promise<LogbookEntry> {
    const headers = await this.getAuthHeaders();
    
    const response = await apiFetch(`${apiConfig.baseURL}/api/logbook/${entryId}`, {
      method: "PATCH",
      headers,
      credentials: "include",
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: "Failed to update logbook entry" }));
      throw new Error(error.error || error.message || "Failed to update logbook entry");
    }

    const data = await response.json();
    return data.entry;
  }

  /**
   * Delete a logbook entry
   */
  async deleteEntry(entryId: string): Promise<void> {
    const headers = await this.getAuthHeaders();
    
    const response = await apiFetch(`${apiConfig.baseURL}/api/logbook/${entryId}`, {
      method: "DELETE",
      headers,
      credentials: "include",
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: "Failed to delete logbook entry" }));
      throw new Error(error.error || error.message || "Failed to delete logbook entry");
    }
  }

  /**
   * Get a single logbook entry by ID
   */
  async getEntry(entryId: string): Promise<LogbookEntry> {
    const headers = await this.getAuthHeaders();
    
    const response = await apiFetch(`${apiConfig.baseURL}/api/logbook/${entryId}`, {
      method: "GET",
      headers,
      credentials: "include",
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: "Failed to fetch logbook entry" }));
      throw new Error(error.error || error.message || "Failed to fetch logbook entry");
    }

    const data = await response.json();
    return data.entry as LogbookEntry;
  }

  /**
   * Get logbook entries
   */
  async getEntries(filters?: {
    callsign?: string;
    band?: Band;
    mode?: Mode;
    country?: string;
    dxcc?: number;
    cqZone?: number;
    ituZone?: number;
    qslStatus?: QSLStatus;
    dateFrom?: string;
    dateTo?: string;
    contestName?: string;
    gridSquare?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  }): Promise<LogbookEntryListResponse> {
    const headers = await this.getAuthHeaders();
    
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.append(key, value.toString());
        }
      });
    }

    const response = await apiFetch(
      `${apiConfig.baseURL}/api/logbook?${params.toString()}`,
      {
        method: "GET",
        headers,
        credentials: "include",
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: "Failed to fetch logbook entries" }));
      throw new Error(error.error || error.message || "Failed to fetch logbook entries");
    }

    return await response.json();
  }

  /**
   * Get logbook statistics
   */
  async getStatistics(filters?: {
    callsign?: string;
    band?: Band;
    mode?: Mode;
    dateFrom?: string;
    dateTo?: string;
  }): Promise<LogbookStatistics> {
    const headers = await this.getAuthHeaders();
    
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.append(key, value.toString());
        }
      });
    }

    const response = await apiFetch(
      `${apiConfig.baseURL}/api/logbook/statistics?${params.toString()}`,
      {
        method: "GET",
        headers,
        credentials: "include",
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: "Failed to fetch statistics" }));
      throw new Error(error.error || error.message || "Failed to fetch statistics");
    }

    return await response.json();
  }

  /**
   * Export logbook to ADIF
   */
  async exportADIF(filters?: {
    dateFrom?: string;
    dateTo?: string;
    band?: Band;
    mode?: Mode;
  }): Promise<Blob> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.append(key, value.toString());
        }
      });
    }

    const response = await apiFetch(
      `${apiConfig.baseURL}/api/logbook/export?${params.toString()}`,
      {
        method: "GET",
        credentials: "include",
        timeoutMs: UPLOAD_API_TIMEOUT_MS,
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: "Failed to export logbook" }));
      throw new Error(error.error || error.message || "Failed to export logbook");
    }

    return await response.blob();
  }

  /**
   * Import logbook from ADIF
   */
  async importADIF(adifContent: string): Promise<{ imported: number; errors: string[] }> {
    const headers = await this.getAuthHeaders();
    
    const response = await apiFetch(`${apiConfig.baseURL}/api/logbook/import`, {
      method: "POST",
      headers,
      credentials: "include",
      body: JSON.stringify({ adifContent }),
      timeoutMs: UPLOAD_API_TIMEOUT_MS,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: "Failed to import logbook" }));
      throw new Error(error.error || error.message || "Failed to import logbook");
    }

    return await response.json();
  }
}

export const logbookService = new LogbookService();

